import { WidgetContainer, AdditionalInfo, ActionButton, NavLink, CircleImages } from '@chewy/chat-library-components';
import styles from './ManageAccountCard.module.scss';
import { first } from 'lodash';
import { ActionDef, Pet } from '@chewy/chat-library-common';
import { getPetImageUrl } from './PetCards/petImages';


interface AdditionalMetaData {
  key?: string;
  value?: string;
}

interface ManageAccountCardData {
  accountId?: string;
  title?: string;
  isLoyalty?: boolean;
  pets?: Pet[];
  additionalMetaData?: AdditionalMetaData[];
  actions?: ActionDef[];
}

interface ManageAccountCardProps {
  data: ManageAccountCardData | ManageAccountCardData[];
  isSelectable?: boolean;
}

export default function ManageAccountCard({ data, isSelectable = false }: ManageAccountCardProps) {
  // Handle both single object and array structure from widgets
  const actualData = Array.isArray(data) ? data[0] : data;

  const title = actualData.title ?? '';
  const isLoyalty = actualData.isLoyalty ?? false;
  const pets = actualData.pets ?? [];
  const additionalMetaData = actualData.additionalMetaData ?? [];
  const hasAdditionalInfo = additionalMetaData.length > 0;
  const actions = actualData.actions ?? [];
  const nonLinkActions = actions.filter((action) => action.type !== 'link');
  const linkActions = actions.filter((action) => action.type === 'link');
  const hasLinkAction = linkActions.length > 0;
  const linkAction = first(linkActions)?.url;
  const hasPets = pets.length > 0;

  return (
    <WidgetContainer
      isSelectable={isSelectable}
      hasActions={actions.length > 0}
      data={actualData}
    >
      <div className={styles.accountContainer}>
        <div className={styles.primaryInfo}>
          <CircleImages
            images={pets.map((pet) => ({
              id: pet.id,
              src: getPetImageUrl(pet.imageUrls?.[0], pet.breedType),
              alt: pet.name || 'Pet image',
            }))}
          />

          <div className={styles.accountInfo}>
            <p className={styles.accountTitle}>{title}</p>
            {hasPets && (
              <p className={styles.accountName}>
                {pets.length <= 2
                  ? pets.map(pet => pet.name).join(', ')
                  : `${pets.slice(0, 2).map(pet => pet.name).join(', ')} + ${pets.length - 2} more`
                }
              </p>
            )}

          </div>

          {isLoyalty && (
            <div>
              <img
                src="/chewy+.svg"
                alt="Chewy+ Loyalty Member"
                className={styles.loyaltyImage}
              />
            </div>
          )}
        </div>

        {hasAdditionalInfo && (
          <div className={styles.additionalInfoContainer}>
            <AdditionalInfo additionalInfo={additionalMetaData} type="account" />
          </div>
        )}

        {(nonLinkActions.length > 0 || hasLinkAction) && (
          <div className={styles.actionsContainer}>
            {nonLinkActions.map((action, index) => (
              <ActionButton key={index} action={action} widgetData={actualData} />
            ))}
            {hasLinkAction && (
              <div className={styles.linkActionContainer}>
                <NavLink
                  href={linkAction || ''}
                  label={first(linkActions)?.text || ''}
                  action={first(linkActions)!}
                  widgetData={actualData}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </WidgetContainer>
  );
}
